import { useCallback, useMemo } from "react";

// Reads the entity definition and the per-type table config out of the merged
// pageMeta/orgMeta payload.
//
// `metaInfo` MUST be usePageMeta's `pagesContent.pageMeta`, not `pagesContent`
// itself. The hook returns a hand-built wrapper (usePageMeta.js:63-71) and the
// payload sits one level down -- passing the wrapper makes every lookup here
// undefined and the page renders "not configured" forever.
//
// Two configs, two jobs (network-tab-spec.md section 1.3):
//   metaInfo.network.entityDefinition -> what the entity IS (orgMeta only)
//   metaInfo.network.detailViews      -> how one record DISPLAYS (orgMeta only)
//   metaInfo.table[tableKey]          -> how its list RENDERS (pageMeta default)
//
// The link between them is `tableKey`, declared on each type rather than
// derived from its code, so a rename cannot silently unhook a table.

const EMPTY = Object.freeze([]);

export const useEntityTypeConfig = (metaInfo) => {
    const types = useMemo(
        () => metaInfo?.network?.entityDefinition?.types || EMPTY,
        [metaInfo]
    );

    // Named lookup, never positional. MySQL normalises JSON object key order,
    // so metaInfo.table comes back in a different order than it was written --
    // indexing it by position would attach the wrong columns to a tab.
    const getTableConfig = useCallback(
        (entityType) =>
            (entityType?.tableKey && metaInfo?.table?.[entityType.tableKey]) || null,
        [metaInfo]
    );

    // columnOrdering is the display order; columnsConfig holds the definitions.
    // A key in the ordering with no config is skipped rather than rendered
    // blank, matching how usePageMeta treats the same mismatch.
    const getSearchableFields = useCallback(
        (entityType) => getTableConfig(entityType)?.searchableFields || EMPTY,
        [getTableConfig]
    );

    // How a record is DISPLAYED, kept out of entityDefinition on purpose: that
    // declares what the entity is and the backend enforces it, whereas this is
    // presentation and belongs beside `table` and `filterConfigurations`.
    //
    //   metaInfo.network.detailViews[<entityTypeCode>] = { layout, sections }
    //
    // Absent means the popup, which is the right default for a type with no
    // linked records to show.
    const getDetailView = useCallback(
        (entityType) =>
            (entityType?.code &&
                metaInfo?.network?.detailViews?.[entityType.code]) || null,
        [metaInfo]
    );

    return {
        types,
        getTableConfig,
        getSearchableFields,
        getDetailView,
        // Only meaningful once the caller has confirmed the payload arrived:
        // an unloaded metaInfo is also length 0. AssetManagement.jsx gates on
        // pagesContent.mounted before rendering, so reaching here with an
        // empty list really does mean the org has no entity types.
        isConfigured: types.length > 0,
    };
};

export default useEntityTypeConfig;
